import React, { memo, useCallback, useMemo } from 'react';
import type { ThemeColors } from '../../shared/theme';
import { useTranslation } from '../../shared/i18n';
import { JOURNEY_GOAL_OPTIONS, type JourneyGoal } from '../../data/domains/journey';
import { JourneyOptionStep, type JourneyOptionItem } from './JourneyOptionStep';
import type { JourneyIconMotion } from './JourneyIconMotionWrap';

const GOAL_ICON_MOTION: Record<Exclude<JourneyGoal, ''>, JourneyIconMotion> = {
  loss: 'loss',
  gain: 'gain',
  between: 'balance',
};

type Props = {
  colors: ThemeColors;
  goal: JourneyGoal;
  on_select_goal: (value: JourneyGoal) => void;
};

export const JourneyGoalStep = memo(function JourneyGoalStep({ colors, goal, on_select_goal }: Props) {
  const { t } = useTranslation();

  const options = useMemo<JourneyOptionItem[]>(
    () =>
      JOURNEY_GOAL_OPTIONS.map(option => ({
        id: String(option.value),
        labelKey: option.labelKey,
        subtitleKey: option.subtitleKey,
        icon: option.icon,
        tone: option.tone,
        icon_motion: GOAL_ICON_MOTION[option.value as Exclude<JourneyGoal, ''>] ?? 'none',
      })),
    [],
  );

  const on_select = useCallback(
    (id: string) => {
      if (id === 'loss' || id === 'gain' || id === 'between') {
        on_select_goal(id);
      }
    },
    [on_select_goal],
  );

  return (
    <JourneyOptionStep
      colors={colors}
      title={t('journey.whatsYourGoal')}
      subtitle={t('journey.wellTailorPlan')}
      options={options}
      selected_id={goal}
      on_select={on_select}
      accessibility_label={t('journey.whatsYourGoal')}
      test_id_prefix="journey-goal"
      emphasis="goal"
    />
  );
});
